/**
 * useDownloadDataset — hook для скачивания обработанного dataset'а как .xlsx
 * (Phase 2.2 — Files UX Refactor).
 *
 * Backend endpoint: GET /api/v1/datasets/{dataset_id}/download
 *  - 200 + xlsx (application/vnd.openxmlformats-...)
 *  - Content-Disposition: attachment; filename="<sanitized>.xlsx"
 *  - 404 на чужой / soft-deleted dataset
 *  - 401 без auth
 *
 * Контракт:
 *  - Auth через Authorization header (api interceptor уже инжектит JWT).
 *  - Token НЕ попадает в URL — никакого window.location / GET-redirect.
 *  - Blob-marshalling + URL.createObjectURL + revoke после click.
 *  - Filename из Content-Disposition (RFC 6266 fallback — ASCII filename).
 *  - Параллельный клик НЕ стартует второй запрос — `mutation.isPending` блокирует
 *    UI через `disabled`.
 *  - Inline error через `error` (mutation), не silent.
 *  - Lifecycle-safe: revoke URL после click (даже если Multiple ANCHOR-s в DOM).
 *
 * Возвращает:
 *  - download(datasetId): Promise<void> — fire-and-forget.
 *  - isPending: boolean — disable UI.
 *  - error: Error | null — inline error.
 *  - reset(): void — сбросить error state.
 */
import { useMutation } from '@tanstack/react-query';

import api from '@/lib/api';

/**
 * Извлекает filename из Content-Disposition header.
 * Поддерживает RFC 6266 `filename="..."` (ASCII fallback достаточно — бэкенд
 * санитизирует non-ASCII в ASCII-фолбэк; UTF-8 `filename*` НЕ используется).
 *
 * Если header отсутствует / malformed — возвращает fallback.
 */
function extractFilename(
  contentDisposition: string | undefined,
  fallback: string,
): string {
  if (!contentDisposition) return fallback;
  // Стандартный pattern: filename="..." или filename=...
  const match = contentDisposition.match(
    /filename\s*=\s*(?:"([^"]+)"|([^;]+))/i,
  );
  if (!match) return fallback;
  const filename = (match[1] || match[2] || '').trim();
  return filename || fallback;
}

export function useDownloadDataset() {
  return useMutation({
    mutationFn: async (datasetId: string) => {
      // === 1. Fetch xlsx bytes ===
      const r = await api.get<Blob>(`/datasets/${datasetId}/download`, {
        responseType: 'blob',
        // Увеличиваем timeout — большие датасеты могут генерироваться
        // долго (max 50k строк).
        timeout: 120_000,
      });

      // === 2. Extract filename from Content-Disposition ===
      // Phase 4 contract: filename = `<sanitized original stem>.xlsx`.
      // Backend — source of truth; FE не формирует уникальное имя. Если
      // header отсутствует / malformed — fallback = `dataset.xlsx` (то же,
      // что backend даёт при пустом sanitized stem). НЕ используем UUID.
      const filename = extractFilename(
        r.headers['content-disposition'] as string | undefined,
        'dataset.xlsx',
      );

      // === 3. Sanity check Content-Type ===
      const ct = r.headers['content-type'] as string | undefined;
      if (ct && !ct.includes('spreadsheetml')) {
        // Не xlsx — значит бэк вернул JSON с ошибкой (404 → FastAPI default).
        // Парсим blob как JSON и пробрасываем в error path.
        try {
          const text = await (r.data as Blob).text();
          const json = JSON.parse(text);
          const msg = json?.error?.message || json?.detail || 'Download failed';
          throw new Error(msg);
        } catch (e) {
          if (e instanceof Error && e.message !== 'Download failed') {
            // Re-throw if JSON parse failed but original error had a message
            throw e;
          }
          throw new Error('Download failed');
        }
      }

      // === 4. Trigger browser download ===
      const blob = r.data instanceof Blob
        ? r.data
        : new Blob([r.data as BlobPart]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      // Hide from screen-reader / screen — это синтетический click.
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      // Cleanup
      a.remove();
      // Defer revoke — Safari/iOS требует чтобы URL был жив в момент click.
      // Cleanup в microtask — достаточно для современных браузеров.
      setTimeout(() => window.URL.revokeObjectURL(url), 0);

      return { filename, bytes: blob.size };
    },
  });
}
